import React from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';

import * as Yup from 'yup';
import { Formik } from 'formik';

import IconButton from 'components/@extended/IconButton';
import AnimateButton from 'components/@extended/AnimateButton';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import EyeInvisibleOutlined from '@ant-design/icons/EyeInvisibleOutlined';

import api from 'utils/api';
import { setAuth } from 'utils/auth';

export default function AuthRegister({ onSuccess, isAdminMode = false }) {
  const navigate = useNavigate();
  const [showPassword, setShowPassword]               = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [roles, setRoles]                             = React.useState([]);
  const [rolesLoading, setRolesLoading]               = React.useState(true);

  const handleClickShowPassword        = () => setShowPassword((p) => !p);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((p) => !p);
  const handleMouseDownPassword        = (e) => e.preventDefault();

  // Fetch roles on mount
  React.useEffect(() => {
    const fetchRoles = async () => {
      try {
        const { data: body } = await api.get('/role/list');
        const data = body?.data ?? body;
        const list =
          data?.roles ??
          data?.role_list ??
          data?.roleList ??
          data?.list ??
          (Array.isArray(data) ? data : []);
        setRoles(Array.isArray(list) ? list : []);
      } catch {
        setRoles([]);
      } finally {
        setRolesLoading(false);
      }
    };
    fetchRoles();
  }, []);

  return (
    <Formik
      initialValues={{
        name:            '',
        username:        '',
        phone:           '',
        email:           '',
        role_id:         '',
        password:        '',
        confirmPassword: '',
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().max(255).required('Full Name is required'),
        username: Yup.string()
          .required('Username is required')
          .min(3, 'Username must be at least 3 characters')
          .test('no-spaces', 'Username cannot contain leading or trailing spaces', (value) => {
            if (!value) return true;
            return value.trim() === value;
          })
          .matches(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores'),
        phone: Yup.string()
          .matches(/^\+?[0-9]{7,15}$/, 'Enter a valid phone number (7–15 digits)')
          .required('Phone is required'),
        email: Yup.string().email('Must be a valid email').max(255),
        role_id: Yup.string().required('Please select a role'),
        password: Yup.string()
          .min(6, 'Password must be at least 6 characters')
          .required('Password is required'),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref('password')], 'Passwords do not match')
          .required('Please confirm your password'),
      })}
      onSubmit={async (values, { setSubmitting, setErrors, setFieldError }) => {
        try {
          const payload = {
            name:     values.name.trim(),
            username: values.username.trim(),
            phone:    values.phone.trim(),
            password: values.password,
            role_id:  values.role_id,
          };
          if (values.email?.trim()) payload.email = values.email.trim();

          const { data: body } = await api.post('/register', payload);

          if (!body.status) {
            setErrors({ submit: body.message || 'Registration failed' });
            return;
          }

          const token = body.data.token;
          const user  = body.data.user;

          if (!isAdminMode) {
            setAuth(token, user);
          }

          if (onSuccess) {
            onSuccess(user);
          } else {
            navigate('/dashboard');
          }

        } catch (error) {
          if (error.response) {
            const msg   = error.response.data?.message || 'Registration failed';
            const lower = msg.toLowerCase();

            if (lower.includes('name'))          setFieldError('name', msg);
            else if (lower.includes('username')) setFieldError('username', msg);
            else if (lower.includes('phone'))    setFieldError('phone', msg);
            else if (lower.includes('email'))    setFieldError('email', msg);
            else if (lower.includes('password')) setFieldError('password', msg);
            else if (lower.includes('role'))     setFieldError('role_id', msg);
            else                                 setErrors({ submit: msg });

          } else if (error.request) {
            setErrors({ submit: 'Cannot connect to server. Please check if the backend is running.' });
          } else {
            setErrors({ submit: error.message || 'Something went wrong' });
          }
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, submitCount, values }) => {
        const showErr = (field) => Boolean(errors[field] && (touched[field] || submitCount > 0));

        return (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3}>

              {/* ── global error ── */}
              {errors.submit && (
                <Grid size={12}>
                  <Alert severity="error">{errors.submit}</Alert>
                </Grid>
              )}

              {/* ── full name ── */}
              <Grid size={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="name-signup">Full Name *</InputLabel>
                  <OutlinedInput
                    id="name-signup"
                    type="text"
                    name="name"
                    value={values.name}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    fullWidth
                    error={showErr('name')}
                  />
                </Stack>
                {showErr('name') && <FormHelperText error>{errors.name}</FormHelperText>}
              </Grid>

              {/* ── username ── */}
              <Grid size={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="username-signup">Username *</InputLabel>
                  <OutlinedInput
                    id="username-signup"
                    type="text"
                    name="username"
                    value={values.username}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="Enter username"
                    fullWidth
                    error={showErr('username')}
                  />
                </Stack>
                {showErr('username') && <FormHelperText error>{errors.username}</FormHelperText>}
              </Grid>

              {/* ── phone ── */}
              <Grid size={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="phone-signup">Phone *</InputLabel>
                  <OutlinedInput
                    id="phone-signup"
                    type="tel"
                    name="phone"
                    value={values.phone}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="+1234567890"
                    fullWidth
                    error={showErr('phone')}
                  />
                </Stack>
                {showErr('phone') && <FormHelperText error>{errors.phone}</FormHelperText>}
              </Grid>

              {/* ── email (optional) ── */}
              <Grid size={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="email-signup">Email Address (optional)</InputLabel>
                  <OutlinedInput
                    id="email-signup"
                    type="email"
                    name="email"
                    value={values.email}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    fullWidth
                    error={showErr('email')}
                  />
                </Stack>
                {showErr('email') && <FormHelperText error>{errors.email}</FormHelperText>}
              </Grid>

              {/* ── role ── */}
              <Grid size={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="role-signup">Role *</InputLabel>
                  <Select
                    id="role-signup"
                    name="role_id"
                    value={values.role_id}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    displayEmpty
                    fullWidth
                    error={showErr('role_id')}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: '#9e9e9e' }}>
                            {rolesLoading ? 'Loading roles…' : 'Select a role'}
                          </span>
                        );
                      }
                      const found = roles.find((r) => (r.role_id ?? r.id) === selected);
                      return found?.role_name ?? selected;
                    }}
                    disabled={rolesLoading}
                    endAdornment={
                      rolesLoading ? (
                        <InputAdornment position="end" sx={{ mr: 1 }}>
                          <CircularProgress size={16} />
                        </InputAdornment>
                      ) : null
                    }
                  >
                    {roles.length === 0 && !rolesLoading && (
                      <MenuItem disabled value="">
                        No roles available
                      </MenuItem>
                    )}
                    {roles.map((role) => (
                      <MenuItem key={role.role_id ?? role.id} value={role.role_id ?? role.id}>
                        {role.role_name}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
                {showErr('role_id') && <FormHelperText error>{errors.role_id}</FormHelperText>}
              </Grid>

              {/* ── password ── */}
              <Grid size={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="password-signup">Password *</InputLabel>
                  <OutlinedInput
                    id="password-signup"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={values.password}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fullWidth
                    error={showErr('password')}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </Stack>
                {showErr('password') && <FormHelperText error>{errors.password}</FormHelperText>}
              </Grid>

              {/* ── confirm password ── */}
              <Grid size={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="confirmPassword-signup">Confirm Password *</InputLabel>
                  <OutlinedInput
                    id="confirmPassword-signup"
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={values.confirmPassword}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fullWidth
                    error={showErr('confirmPassword')}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowConfirmPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                          {showConfirmPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </Stack>
                {showErr('confirmPassword') && (
                  <FormHelperText error>{errors.confirmPassword}</FormHelperText>
                )}
              </Grid>

              {/* ── submit ── */}
              <Grid size={12}>
                <AnimateButton>
                  <Button
                    disableElevation
                    disabled={isSubmitting}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    {isSubmitting ? 'Creating Account…' : 'Create Account'}
                  </Button>
                </AnimateButton>
              </Grid>

            </Grid>
          </form>
        );
      }}
    </Formik>
  );
}